import { useCallback, useEffect, useMemo, useState } from "react";
import { Select } from "antd";
import { Check, MapPin, RefreshCw, Search, Trash2, Wand2 } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import {
    CITY_STATE_URL, CITY_STATE_FILL_URL, CITY_STATE_MAPPINGS_URL, CITY_STATE_MAPPING_URL,
} from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";

const METHOD_LABELS = {
    manual: "your mappings", in_text: "state written in the city", pincode: "PIN code",
    alias: "known city names", exact: "the India city list", learned: "your own leads",
    preferred: "well-known cities", fuzzy: "close spellings",
};

/**
 * Settings → City → State.
 *
 * Zapier and Meta send a lead's city but never its state. The backend fills
 * the state from the city on its own (customers/geo.py — offline, no third
 * party) whenever it is certain. This page is for the rest: cities it could
 * not place — a locality, a heavy misspelling, a name two states share. An
 * admin picks the state once and every lead with that city, now and later,
 * gets it.
 *
 * Only blank states are ever filled; a state someone set is never changed.
 */
export default function CityStateMapping() {
    const { fetch, create, remove } = useApiMethods();

    const [data, setData]       = useState(null);
    const [loading, setLoading] = useState(false);
    const [filling, setFilling] = useState(false);
    const [picks, setPicks]     = useState({});    // city_key -> state
    const [savingKey, setSavingKey] = useState(null);
    const [query, setQuery]     = useState("");
    // Dis-Qualified leads will not be worked, so their cities are hidden from
    // the to-do list unless asked for. Remembered between visits.
    const [hideDq, setHideDq]   = useState(() => {
        try { return window.localStorage.getItem("cityState.hideDq") !== "0"; } catch { return true; }
    });

    const load = useCallback(() => {
        setLoading(true);
        fetch(CITY_STATE_URL, { hide_disqualified: hideDq ? 1 : 0 })
            .then(res => {
                if (res?.status === "success") {
                    setData(res);
                    // A city two states share opens on its first candidate.
                    const next = {};
                    (res.unmapped || []).forEach(u => {
                        if (u.candidates?.length === 1) next[u.city_key] = u.candidates[0];
                    });
                    setPicks(next);
                } else ToastMsg("error", res?.message || "Could not load cities");
            })
            .catch(() => ToastMsg("error", "Could not load cities"))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hideDq]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        try { window.localStorage.setItem("cityState.hideDq", hideDq ? "1" : "0"); } catch { /* private mode */ }
    }, [hideDq]);

    const canEdit = !!data?.can_edit;
    const s = data?.summary || {};

    const stateOptions = useCallback((candidates = []) => {
        const all = data?.states || [];
        const rest = all.filter(n => !candidates.includes(n));
        return [
            ...(candidates.length ? [{ label: "Likely", options: candidates.map(n => ({ value: n, label: n })) }] : []),
            { label: candidates.length ? "All states" : "States", options: rest.map(n => ({ value: n, label: n })) },
        ];
    }, [data]);

    const unmapped = useMemo(() => {
        const q = query.trim().toLowerCase();
        const list = data?.unmapped || [];
        return q ? list.filter(u => u.city.toLowerCase().includes(q)
                                  || (u.spellings || []).some(x => x.toLowerCase().includes(q)))
                 : list;
    }, [data, query]);

    const fillNow = () => {
        setFilling(true);
        create(CITY_STATE_FILL_URL, {})
            .then(res => {
                if (res?.status === "success") {
                    const parts = Object.entries(res.by_method || {})
                        .sort((a, b) => b[1] - a[1])
                        .map(([m, n]) => `${n} from ${METHOD_LABELS[m] || m}`);
                    ToastMsg("success", res.message + (parts.length ? ` (${parts.join(", ")})` : ""));
                    load();
                } else ToastMsg("error", res?.message || "Could not fill states");
            })
            .catch(() => ToastMsg("error", "Could not fill states"))
            .finally(() => setFilling(false));
    };

    const saveMapping = (u) => {
        const state = picks[u.city_key];
        if (!state) { ToastMsg("error", `Pick a state for ${u.city}`); return; }
        setSavingKey(u.city_key);
        create(CITY_STATE_MAPPINGS_URL, { city: u.city, state })
            .then(res => {
                if (res?.status === "success") { ToastMsg("success", res.message); load(); }
                else ToastMsg("error", res?.message || "Could not save");
            })
            .catch(() => ToastMsg("error", "Could not save"))
            .finally(() => setSavingKey(null));
    };

    const deleteMapping = (m) => {
        if (!window.confirm(`Remove the mapping ${m.city} → ${m.state}? Leads already filled keep their state.`)) return;
        remove(CITY_STATE_MAPPING_URL(m.id))
            .then(res => {
                if (res?.status === "success") { ToastMsg("success", res.message); load(); }
                else ToastMsg("error", res?.message || "Could not remove");
            })
            .catch(() => ToastMsg("error", "Could not remove"));
    };

    const Stat = ({ label, value, hint, tone = "default" }) => (
        <div className={cn("rounded-xl border px-4 py-3",
            tone === "brand" ? "border-brand-200 bg-brand-50 dark:border-brand-800 dark:bg-brand-950/40"
          : tone === "warn"  ? "border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/20"
          : "border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800")}>
            <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400">{label}</p>
            <p className="mt-0.5 text-2xl font-semibold text-gray-900 dark:text-white">{value ?? "—"}</p>
            {hint && <p className="text-[11px] text-gray-500 dark:text-gray-400">{hint}</p>}
        </div>
    );

    return (
        <ContainerCard>
            <div className="p-4 space-y-5">
                {/* ── header ───────────────────────────────────────── */}
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-primary" />
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">City → State</h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400 max-w-2xl">
                                Zapier and Meta send a lead's city but not its state. The state is filled
                                from the city automatically when the system is sure — using the India city
                                list, common names (Bangalore, Trichy, Vizag…), PIN codes and your own
                                leads. Cities it cannot place are listed below: pick the state once and
                                every lead with that city gets it, now and in future.
                            </p>
                        </div>
                    </div>
                    <button onClick={load} title="Reload"
                        className="h-8 w-8 shrink-0 rounded-md border border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-center">
                        <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
                    </button>
                </div>

                {!canEdit && data && (
                    <p className="text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
                        You can view this page; saving mappings needs the City → State edit permission (Settings → Roles).
                    </p>
                )}

                {/* ── summary ──────────────────────────────────────── */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <Stat label="Leads with a city but no state" value={s.missing_state} />
                    <Stat label="Can be filled automatically" value={s.fillable_leads} tone="brand"
                          hint={s.fillable_cities ? `${s.fillable_cities} cities` : "Nothing waiting"} />
                    <Stat label="Need you to pick the state" value={s.unmapped_leads} tone="warn"
                          hint={s.unmapped_cities ? `${s.unmapped_cities} cities` : "All mapped"} />
                    <Stat label="Leads with no city at all" value={s.no_city}
                          hint="Nothing to map from" />
                </div>

                {canEdit && s.fillable_leads > 0 && (
                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-brand-200 bg-brand-50 px-4 py-3 dark:border-brand-800 dark:bg-brand-950/40">
                        <p className="text-sm text-gray-800 dark:text-gray-100">
                            <b>{s.fillable_leads}</b> lead{s.fillable_leads === 1 ? "" : "s"} can get their state now.
                            Only blank states are filled — nothing anyone entered is changed.
                        </p>
                        <button onClick={fillNow} disabled={filling}
                            className="h-9 px-4 rounded-lg text-sm font-medium bg-primary hover:bg-primary/90 disabled:opacity-50 text-white flex items-center gap-1.5">
                            <Wand2 className={cn("h-4 w-4", filling && "animate-pulse")} />
                            {filling ? "Filling…" : "Fill states now"}
                        </button>
                    </div>
                )}

                {/* ── cities to map ────────────────────────────────── */}
                <div>
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                            Cities to map {data && <span className="font-normal text-gray-500">({unmapped.length})</span>}
                        </h3>
                        <div className="flex flex-wrap items-center gap-3">
                        <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 cursor-pointer"
                               title="Dis-Qualified leads will not be worked, so their cities are left out of this list and the counts above">
                            <input type="checkbox" className="h-3.5 w-3.5 accent-primary" checked={hideDq}
                                   onChange={e => setHideDq(e.target.checked)} />
                            Hide Dis-Qualified leads
                            {hideDq && s.disqualified_hidden > 0 && (
                                <span className="text-gray-400">({s.disqualified_hidden} hidden)</span>
                            )}
                        </label>
                        <div className="relative">
                            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Find a city"
                                className="h-8 w-56 rounded-md border border-gray-200 bg-white pl-7 pr-2 text-xs outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-800 dark:text-white" />
                        </div>
                        </div>
                    </div>
                    <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
                        <table className="min-w-full text-xs">
                            <thead>
                                <tr className="bg-gray-50 text-left text-gray-500 dark:bg-gray-700/40 dark:text-gray-400">
                                    <th className="px-3 py-2 font-medium">City as typed</th>
                                    <th className="px-3 py-2 font-medium text-right">Leads</th>
                                    <th className="px-3 py-2 font-medium">State</th>
                                    <th className="px-3 py-2" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {data && unmapped.length === 0 && (
                                    <tr><td colSpan={4} className="px-3 py-6 text-center text-gray-500 dark:text-gray-400">
                                        {query ? "No city matches that search." : "Every city on your leads has a state. 🎉"}
                                    </td></tr>
                                )}
                                {unmapped.map(u => (
                                    <tr key={u.city_key} className="hover:bg-gray-50/60 dark:hover:bg-gray-700/20">
                                        <td className="px-3 py-2">
                                            <p className="font-medium text-gray-900 dark:text-white">{u.city}</p>
                                            {u.spellings?.length > 1 && (
                                                <p className="text-[10px] text-gray-500 dark:text-gray-400">
                                                    also typed: {u.spellings.filter(x => x !== u.city).join(", ")}
                                                </p>
                                            )}
                                            {u.candidates?.length > 1 && (
                                                <p className="text-[10px] text-amber-700 dark:text-amber-300">
                                                    This name is in {u.candidates.join(" and ")}
                                                </p>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{u.leads}</td>
                                        <td className="px-3 py-2 min-w-[14rem]">
                                            <Select size="small" className="w-full" showSearch placeholder="Pick a state"
                                                disabled={!canEdit}
                                                value={picks[u.city_key]}
                                                onChange={v => setPicks(p => ({ ...p, [u.city_key]: v }))}
                                                options={stateOptions(u.candidates)} />
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            {canEdit && (
                                                <button onClick={() => saveMapping(u)}
                                                    disabled={!picks[u.city_key] || savingKey === u.city_key}
                                                    className="h-7 px-2.5 rounded-md text-xs font-medium bg-primary hover:bg-primary/90 disabled:opacity-40 text-white inline-flex items-center gap-1">
                                                    <Check className="h-3.5 w-3.5" />
                                                    {savingKey === u.city_key ? "Saving…" : "Save"}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* ── saved mappings ───────────────────────────────── */}
                {(data?.mappings || []).length > 0 && (
                    <div>
                        <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">
                            Your mappings <span className="font-normal text-gray-500">({data.mappings.length})</span>
                        </h3>
                        <div className="flex flex-wrap gap-2">
                            {data.mappings.map(m => (
                                <span key={m.id}
                                    className="inline-flex items-center gap-1.5 rounded-full border border-brand-200 bg-brand-50 py-1 pl-3 pr-1.5 text-xs text-brand-800 dark:border-brand-800 dark:bg-brand-950/40 dark:text-brand-200">
                                    {m.city} → <b>{m.state}</b>
                                    <span className="text-brand-500">· {m.leads}</span>
                                    {canEdit && (
                                        <button onClick={() => deleteMapping(m)} title="Remove mapping"
                                            className="ml-0.5 rounded-full p-1 text-brand-500 hover:bg-brand-100 hover:text-red-600 dark:hover:bg-brand-900">
                                            <Trash2 className="h-3 w-3" />
                                        </button>
                                    )}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </ContainerCard>
    );
}
